import { useEffect, useState } from "react";
import { Download, X, Share } from "lucide-react";

// Per-portal installable PWA. Points the install manifest + iOS title at the
// current portal so each portal installs as its own home-screen app, and shows
// an install banner (Android/Chrome beforeinstallprompt + iOS Add-to-Home hint).
const PWA = {
  doctor: { manifest: "/manifest-doctor.json", title: "Doctor", label: "PranIQ Doctor" },
  staff: { manifest: "/manifest-doctor.json", title: "Doctor", label: "PranIQ Doctor" },
  pro: { manifest: "/manifest-pro.json", title: "Clinician", label: "PranIQ Clinician" },
  patient: { manifest: "/manifest.json", title: "Patient", label: "PranIQ Patient" },
  admin: { manifest: "/manifest-admin.json", title: "Admin", label: "PranIQ Admin" },
  ops: { manifest: "/manifest-ops.json", title: "Ops", label: "PranIQ Operations" },
  finance: { manifest: "/manifest-finance.json", title: "Finance", label: "PranIQ Finance" },
};

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;

export function PortalPWA({ portal, showBanner = true, className = "" }) {
  const meta = PWA[portal] || PWA.patient;
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  // Point the install manifest + iOS app title at this portal.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (link) link.setAttribute("href", meta.manifest);
    const t = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    if (t) t.setAttribute("content", meta.title);
  }, [meta.manifest, meta.title]);

  useEffect(() => {
    if (!showBanner) return undefined;
    if (isStandalone() || localStorage.getItem(`pq_install_dismissed_${portal}`)) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    const ua = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua) || (/(macintosh)/.test(ua) && "ontouchend" in document);
    if (isIOS && !window.navigator.standalone) { setShow(true); setIosHint(true); }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [portal, showBanner]);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome) { setShow(false); setDeferred(null); }
  };
  const dismiss = () => { setShow(false); localStorage.setItem(`pq_install_dismissed_${portal}`, "1"); };

  if (!showBanner || !show) return null;

  return (
    <div className={`mb-4 rounded-2xl bg-[#0A1B33] text-white p-3.5 flex items-center gap-3 shadow-lg ${className}`} data-testid="pwa-install-banner">
      <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
        <img src="/praniq-mark.png" alt="PranIQ" className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold">Install {meta.label}</div>
        {iosHint ? (
          <div className="text-[11.5px] text-slate-300 flex items-center gap-1">Tap <Share size={12} /> then "Add to Home Screen"</div>
        ) : (
          <div className="text-[11.5px] text-slate-300">Add it to your home screen for a full-screen app.</div>
        )}
      </div>
      {!iosHint && (
        <button onClick={install} data-testid="pwa-install-btn"
          className="flex items-center gap-1.5 bg-[#DAA520] text-[#0A1B33] font-bold text-[12.5px] px-3 py-2 rounded-lg shrink-0">
          <Download size={14} /> Install
        </button>
      )}
      <button onClick={dismiss} data-testid="pwa-install-dismiss" className="text-slate-400 hover:text-white p-1 shrink-0"><X size={16} /></button>
    </div>
  );
}
